import type { Vertex } from "../types";

/**
 * Performs spherical linear interpolation (SLERP) between two 3D vertices.
 * @param start - The starting vertex (unit vector) for interpolation.
 * @param end - The ending vertex (unit vector) for interpolation.
 * @param interpolationFactor - A number between 0 and 1 that controls the interpolation;
 *                              0 returns the 'start' vector, 1 returns the 'end' vector.
 * @returns The interpolated vertex computed via spherical linear interpolation.
 */
function slerpVectors(start: Vertex, end: Vertex, interpolationFactor: number): Vertex {
  const dot: number = start[0] * end[0] + start[1] * end[1] + start[2] * end[2];
  const clampedDot: number = Math.min(Math.max(dot, -1), 1);
  const theta: number = Math.acos(clampedDot);

  if (theta < 1e-6) return start;

  const sinTheta: number = Math.sin(theta);
  const factorStart: number = Math.sin((1 - interpolationFactor) * theta) / sinTheta;
  const factorEnd: number = Math.sin(interpolationFactor * theta) / sinTheta;

  return [
    start[0] * factorStart + end[0] * factorEnd,
    start[1] * factorStart + end[1] * factorEnd,
    start[2] * factorStart + end[2] * factorEnd,
  ];
}

export { slerpVectors };
